import React, { forwardRef, useImperativeHandle } from 'react';
import { upperFirst } from 'lodash';

import { AdvanceTable, IBasicTableColumn, IconTextButton, Label, Skeleton } from 'ui-lib';
import { getErrorMessage } from 'common-utils';
import {
    ManageDeviceJobStore,
    IDisplayJobExecutionSummary,
} from 'store/device-details/device-jobs-store';
import { getJobNameFromCode, showConfirmation, showNotification } from '../utils';

import devicesCss from './devices.css';
import { usePagination } from 'hooks/use-table-pagination.hook';
import { formatDate } from 'common-utils/date-utils';
import { MessagesAlert } from 'pages/private/components/alert/messages-alert';
import { TableUtils } from 'pages/private/components/table-utils';
import { IDeviceTableProps } from './components';
import { useDeviceStore } from 'store';

export const DeviceJobTable = forwardRef((props: IDeviceTableProps, ref) => {
    const devicesStore = useDeviceStore();
    const memoizedStore = React.useMemo(() => {
        return new ManageDeviceJobStore({
            accountId: props.accountId,
            uid: props.uid,
            devicesStore: devicesStore,
            serialNumber: props.serialNumber,
        });
    }, []);

    const { pagination, data, loading, refresh, errorMessages, refreshPage, onTableChange } = usePagination({
        pageSize: 10,
        fetchData: async (params) => {
            const { pageSize, nextToken } = params;
            const result = await memoizedStore.loadJobs(pageSize, nextToken);
            return {
                data: result.jobExecutionSummaries,
                nextToken: result.nextToken,
            };
        },
        enableShowTotal: true,
        cacheKey: 'device-jobs-table',
        mode: 'cursor',
    });

    const [jobExecutionDetailsMap, setJobExecutionDetailsMap] = React.useState(new Map<string, any>());
    useImperativeHandle(ref, () => {
        return {
            refresh,
        };
    }, []);
    const onAction = (job: IDisplayJobExecutionSummary) => {
        const jobActions = {
            type: 'Cancel',
            message: 'Job cancellation has been started.',
            action: async () => memoizedStore.cancelExecutionCommand(job),
        };
        if (job.rerunable) {
            Object.assign(jobActions, {
                type: 'Re-run',
                message: 'Job re-run has been started.',
                action: async () => memoizedStore.rerunExecutionCommand(job),
            });
        }
        showConfirmation(`Are you sure you want to ${jobActions.type} this job?`, async () => {
            showNotification('info', jobActions.message);
            try {
                await jobActions.action();
                await refreshPage();
            } catch (error) {
                showNotification('error', getErrorMessage(error));
            }
        });
    };

    const columns: IBasicTableColumn[] = [
        TableUtils.createBasicFilterColumn({ key: 'jobName', title: 'Job Name' }),
        {
            code: 'jobId',
            title: 'Job ID',
            dataIndex: 'jobId',
            render: (_, record: IDisplayJobExecutionSummary) => {
                return !record.ownerJob ? (
                    record.jobId
                ) : (
                    <React.Fragment>
                        <Label label={record.jobId} />
                        <br />
                        <Label label={`(${record.ownerJob.jobId})`} />
                    </React.Fragment>
                );
            },
        },
        { code: 'status', title: 'Status', dataIndex: 'status' },
        {
            code: 'displayLastUpdatedAt',
            title: 'Last Updated At',
            dataIndex: 'displayLastUpdatedAt',
            sorter: (a: IDisplayJobExecutionSummary, b: IDisplayJobExecutionSummary) =>
                a.lastUpdatedAt?.localeCompare(b.lastUpdatedAt),
        },
        {
            code: 'actions',
            title: 'Action',
            dataIndex: 'jobId',
            render: (_, record: IDisplayJobExecutionSummary) => {
                const jobStatus = record['status'];
                return (
                    <IconTextButton
                        label={record.rerunable ? 'Re-run' : 'Cancel'}
                        type='link'
                        disabled={!record.cancelable && !record.rerunable}
                        onClick={() => onAction(record)}
                    />
                );
            },
        },
    ];

    const expandedRowRender = (row: IDisplayJobExecutionSummary) => {
        const responsePayload = jobExecutionDetailsMap[row.key];

        const innerColumns = Object.keys(responsePayload || [])?.map((key) => {
            return {
                code: key,
                title: upperFirst(key),
                dataIndex: key,
            };
        });
        return (
            <Skeleton loading={responsePayload == undefined} noOfRows={1}>
                <React.Fragment>
                    {responsePayload && innerColumns.length > 0 ? (
                        <AdvanceTable
                            title={'Job Execution Message'}
                            table={{ columns: innerColumns, dataSource: [{ ...responsePayload, key: row.key }] }}
                        />
                    ) : (
                        <Label label={'No response message'} />
                    )}
                </React.Fragment>
            </Skeleton>
        );
    };

    const displayableData = React.useMemo(() => {
        return data?.map((job) => {
            const jobName = getJobNameFromCode(job.jobId);
            return {
                ...job,
                key: `${job.jobId}-${job.lastUpdatedAt}`,
                jobName: jobName,
                displayLastUpdatedAt: formatDate(job.lastUpdatedAt),
            };
        });
    }, [data]);

    return (
        <div className={devicesCss.tabPane}>
            <MessagesAlert errorMessages={errorMessages} />
            <AdvanceTable
                title=''
                table={{
                    dataSource: displayableData,
                    columns: columns,
                    pagination,
                    onTableChange,
                    expandable: {
                        expandedRowRender,
                        onExpand: async (expanded: boolean, record: any) => {
                            if (!expanded) return;
                            const data = await memoizedStore.getDetailsByExecution(record);
                            setJobExecutionDetailsMap((prev) => {
                                return {
                                    ...prev,
                                    [record.key]: data,
                                };
                            });
                        },
                    },
                }}
                loadingState={loading}
                toolbar={{
                    onReload: () => refresh(),
                }}
            />
        </div>
    );
});
